import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Loading, Btn, EmptyState } from "@/components/primitives";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { CalendarOff, Plus, Check, X, Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls = "w-full h-10 px-3 rounded-md border border-curtron-line bg-curtron-surface text-sm outline-none focus:border-curtron-green transition";
const labelCls = "text-[11px] uppercase tracking-wide font-medium text-curtron-sub";
const REASONS = [["vacation", "Vacation"], ["sick", "Sick"], ["personal", "Personal"], ["bereavement", "Bereavement"], ["jury_duty", "Jury Duty"], ["other", "Other"]];
const STATUS_PILL = {
  pending: "bg-curtron-ochre/15 text-curtron-ochre",
  approved: "bg-curtron-green/15 text-curtron-green",
  denied: "bg-curtron-terracotta/15 text-curtron-terracotta",
  rejected: "bg-curtron-terracotta/15 text-curtron-terracotta",
};
const STATUS_LABEL = { pending: "Pending", approved: "Approved", denied: "Denied", rejected: "Denied" };

export default function TimeOff() {
  const { user, isAdmin } = useAuth();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({ reason: "vacation", unit: "days", quantity: "", beginning_on: "", ending_on: "", reason_other_text: "", additional_comments: "" });

  const load = () => api.get("/time-off-requests").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.beginning_on || !form.ending_on) { toast.error("Choose start and end dates"); return; }
    setSaving(true);
    try {
      await api.post("/time-off-requests", {
        ...form,
        quantity: form.quantity ? Number(form.quantity) : null,
      });
      toast.success("Time-off request submitted");
      setOpen(false);
      setForm({ reason: "vacation", unit: "days", quantity: "", beginning_on: "", ending_on: "", reason_other_text: "", additional_comments: "" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const decide = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/time-off-requests/${id}`, { status });
      toast.success(`Request ${status}`);
      load();
    } catch (e) {
      toast.error(apiError(e) || "Approve/deny isn't available on the server yet.");
    } finally { setBusyId(null); }
  };

  const cancel = async (id) => {
    setBusyId(id);
    try { await api.delete(`/time-off-requests/${id}`); toast.success("Request withdrawn"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };

  if (items === null) return <Loading />;

  return (
    <div className="max-w-3xl">
      <PageHeader title="Time-Off Requests" subtitle={isAdmin ? "Review and approve team time-off." : "Request and track your time off."}>
        <Btn onClick={() => setOpen(true)} data-testid="new-timeoff-btn"><Plus className="w-4 h-4" /> New request</Btn>
      </PageHeader>

      {items.length === 0 ? (
        <Card><EmptyState icon={CalendarOff} title="No requests" subtitle="Submit a time-off request to get started." /></Card>
      ) : (
        <div className="space-y-3">
          {items.map((r) => {
            const mine = r.user_id === user?.id;
            const reasonLabel = (REASONS.find((x) => x[0] === r.reason)?.[1]) || r.reason || "—";
            return (
              <Card key={r.id} className="p-4" data-testid={`timeoff-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-curtron-ink text-sm">{r.employee_name || "—"} · {reasonLabel}{r.reason === "other" && r.reason_other_text ? ` (${r.reason_other_text})` : ""}</div>
                    <div className="text-sm text-curtron-sub mt-0.5">{fmtDate(r.beginning_on, false)} → {fmtDate(r.ending_on, false)}{r.quantity ? ` · ${r.quantity} ${r.unit}` : ""}</div>
                    {r.additional_comments && <div className="text-xs text-curtron-muted mt-1">{r.additional_comments}</div>}
                    {r.manager_note && <div className="text-xs text-curtron-muted mt-1">Note: {r.manager_note}</div>}
                  </div>
                  <span className={`text-[10px] uppercase font-semibold tracking-wide rounded-full px-2 py-0.5 shrink-0 ${STATUS_PILL[r.status] || "bg-curtron-muted/15 text-curtron-muted"}`} data-testid={`timeoff-status-${r.id}`}>{STATUS_LABEL[r.status] || r.status}</span>
                </div>

                {(isAdmin && r.status === "pending") && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-curtron-line">
                    <Btn className="h-8" onClick={() => decide(r.id, "approved")} disabled={busyId === r.id} data-testid={`timeoff-approve-${r.id}`}>{busyId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve</Btn>
                    <Btn variant="secondary" className="h-8 text-curtron-terracotta" onClick={() => decide(r.id, "denied")} disabled={busyId === r.id} data-testid={`timeoff-deny-${r.id}`}><X className="w-4 h-4" /> Deny</Btn>
                  </div>
                )}
                {mine && r.status === "pending" && !isAdmin && (
                  <div className="mt-3 pt-3 border-t border-curtron-line">
                    <Btn variant="secondary" className="h-8 text-curtron-terracotta" onClick={() => cancel(r.id)} disabled={busyId === r.id} data-testid={`timeoff-cancel-${r.id}`}><Trash2 className="w-4 h-4" /> Withdraw</Btn>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Time-Off Request</DialogTitle>
            <DialogDescription>Submit a request for your manager to review.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Reason</label>
                <select value={form.reason} onChange={(e) => set("reason", e.target.value)} className={`${inputCls} mt-1`} data-testid="timeoff-reason">
                  {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Unit</label>
                <select value={form.unit} onChange={(e) => set("unit", e.target.value)} className={`${inputCls} mt-1`} data-testid="timeoff-unit">
                  <option value="days">Days</option><option value="hours">Hours</option>
                </select>
              </div>
            </div>
            {form.reason === "other" && (
              <div><label className={labelCls}>Please specify</label>
                <input value={form.reason_other_text} onChange={(e) => set("reason_other_text", e.target.value)} className={`${inputCls} mt-1`} data-testid="timeoff-reason-other" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>From</label><input type="date" value={form.beginning_on} onChange={(e) => set("beginning_on", e.target.value)} className={`${inputCls} mt-1`} data-testid="timeoff-from" /></div>
              <div><label className={labelCls}>To</label><input type="date" value={form.ending_on} onChange={(e) => set("ending_on", e.target.value)} className={`${inputCls} mt-1`} data-testid="timeoff-to" /></div>
            </div>
            <div><label className={labelCls}>Quantity (optional)</label><input type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} placeholder={`Number of ${form.unit}`} className={`${inputCls} mt-1`} data-testid="timeoff-quantity" /></div>
            <div><label className={labelCls}>Comments (optional)</label><textarea value={form.additional_comments} onChange={(e) => set("additional_comments", e.target.value)} rows={2} className={`${inputCls} mt-1 h-auto py-2`} data-testid="timeoff-comments" /></div>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn onClick={submit} disabled={saving} data-testid="timeoff-submit">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Submit</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
